import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import {
  PageContainer,
  MainCard,
  FooterSection
} from './Layout';
import { formatDate, formatExpirationDate } from '../utils/dateUtils';
import { getDaysUntilExpiry } from '../utils/dateValidation';
import { generateAndDownloadCalendar } from '../utils/calendarUtils';
import { FiClock, FiAlertTriangle, FiCheck, FiX, FiCalendar, FiChevronLeft, FiRefreshCw } from 'react-icons/fi';
import { TimerContainer, TimerWrapper, GradientDefs, ResponsiveTimer } from './Timer';
import ReactCountryFlag from 'react-country-flag';
import { TimeAccuracyInfo } from './TimeAccuracyInfo';
import { locationService } from '../services/locationService';

// Status color helper function
const getStatusColors = (status, isExpired, theme) => {
  if (isExpired) {
    return {
      background: theme.colors.status.expired.light,
      border: theme.colors.status.expired.border,
      solid: theme.colors.status.expired.main,
      text: theme.colors.status.expired.main
    };
  }

  switch (status) {
    case 'Valid':
    case 'valid':
      return {
        background: theme.colors.status.valid.light,
        border: theme.colors.status.valid.border,
        solid: theme.colors.status.valid.main,
        text: theme.colors.status.valid.main
      };
    case 'urgent':
      return {
        background: theme.colors.status.urgent.background,
        border: theme.colors.status.urgent.border,
        solid: theme.colors.status.urgent.main,
        text: theme.colors.status.urgent.main
      };
    case 'warning':
      return {
        background: theme.colors.status.warning.background,
        border: theme.colors.status.warning.border,
        solid: theme.colors.status.warning.main,
        text: theme.colors.status.warning.main
      };
    default:
      return {
        background: `${theme.colors.primary}1A`,
        border: `${theme.colors.primary}4D`,
        solid: theme.colors.primary,
        text: theme.colors.primary
      };
  }
};

const StatusContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  position: relative;
`;

const BackButton = styled.button`
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.text.primary};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  padding: ${({ theme }) => theme.spacing.xs} 0;
  transition: opacity 0.2s ease;
  min-height: ${({ theme }) => theme.mobile.touchTarget};

  @media (min-width: ${({ theme }) => theme.breakpoints.mobileLarge}) {
    gap: ${({ theme }) => theme.spacing.sm};
    font-size: ${({ theme }) => theme.typography.fontSize.lg};
    margin-bottom: ${({ theme }) => theme.spacing.xl};
  }

  &:hover {
    opacity: 0.8;
  }
`;

const StatusHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  line-height: 1.4;

  @media (min-width: ${({ theme }) => theme.breakpoints.mobileLarge}) {
    gap: ${({ theme }) => theme.spacing.lg};
    margin-bottom: ${({ theme }) => theme.spacing.xl};
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    margin-bottom: ${({ theme }) => theme.spacing.xxl};
  }
`;

const StatusHeaderContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const StatusIconWrapper = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: ${({ theme, $isExpired }) => $isExpired ? theme.colors.status.expired.main : theme.colors.status.valid.main};
  display: flex;
  align-items: center;
  justify-content: center;
  color: #0F172A;
  box-shadow: 0 0 20px ${({ theme, $isExpired }) => $isExpired ? theme.colors.status.expired.light : theme.colors.status.valid.light};
  flex-shrink: 0;

  @media (min-width: ${({ theme }) => theme.breakpoints.mobileLarge}) {
    width: 64px;
    height: 64px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    width: 80px;
    height: 80px;
  }

  svg {
    width: 24px;
    height: 24px;

    @media (min-width: ${({ theme }) => theme.breakpoints.mobileLarge}) {
      width: 32px;
      height: 32px;
    }

    @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
      width: 40px;
      height: 40px;
    }
  }
`;

const StatusText = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.primary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  line-height: 1.3;
  text-align: left;
  width: 100%;

  @media (min-width: ${({ theme }) => theme.breakpoints.mobileLarge}) {
    font-size: ${({ theme }) => theme.typography.fontSize.xl};
    letter-spacing: 1px;
    line-height: 1.2;
  }
`;

const StatusValue = styled.span`
  display: block;
  color: ${({ theme, $isExpired }) => $isExpired ? theme.colors.status.expired.main : theme.colors.status.valid.main};
  font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
  line-height: 1.2;
  text-align: left;

  @media (min-width: ${({ theme }) => theme.breakpoints.mobileLarge}) {
    font-size: ${({ theme }) => theme.typography.fontSize['3xl']};
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: ${({ theme }) => theme.typography.fontSize['4xl']};
  }
`;

const StatusFlagWrapper = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  overflow: hidden;
  border: 2px solid rgba(255, 255, 255, 0.1);
  flex-shrink: 0;

  @media (min-width: ${({ theme }) => theme.breakpoints.mobileLarge}) {
    width: 64px;
    height: 64px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    width: 80px;
    height: 80px;
  }
`;

const CountryInfoWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.xs} 0;
  text-align: left;

  @media (min-width: ${({ theme }) => theme.breakpoints.mobileLarge}) {
    gap: ${({ theme }) => theme.spacing.lg};
    padding: ${({ theme }) => theme.spacing.sm} 0;
  }
`;

const StatusCountryName = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.normal};
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.4;
  word-break: break-word;
  overflow-wrap: break-word;
  text-align: left;
  letter-spacing: 0.5px;
  text-transform: uppercase;

  @media (min-width: ${({ theme }) => theme.breakpoints.mobileLarge}) {
    font-size: ${({ theme }) => theme.typography.fontSize.base};
  }
`;

const StatusVisaDuration = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  margin-top: ${({ theme }) => theme.spacing.xs};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: block;
  width: 100%;
  line-height: 1.4;
  text-align: left;

  @media (min-width: ${({ theme }) => theme.breakpoints.mobileLarge}) {
    font-size: ${({ theme }) => theme.typography.fontSize.base};
    margin-top: ${({ theme }) => theme.spacing.sm};
  }
`;

const DetailCard = styled.div`
  background: rgba(30, 41, 59, 0.4);
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  border: 1px solid rgba(255, 255, 255, 0.05);

  @media (min-width: ${({ theme }) => theme.breakpoints.mobileLarge}) {
    padding: ${({ theme }) => theme.spacing.lg};
  }
`;

const CountryCard = styled(DetailCard)`
  background: rgba(0, 255, 128, 0.1);
  border: 1px solid rgba(0, 255, 128, 0.3);
`;

const StatusCard = styled(DetailCard)`
  background: ${({ $isExpired, theme }) => 
    $isExpired ? 'rgba(30, 41, 59, 0.4)' : 'rgba(0, 255, 128, 0.1)'};
  border: ${({ $isExpired, theme }) => 
    $isExpired ? '1px solid rgba(255, 255, 255, 0.05)' : '1px solid rgba(0, 255, 128, 0.3)'};
`;

const DetailTitle = styled.h4`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  margin: 0 0 ${({ theme }) => theme.spacing.sm} 0;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  line-height: 1.4;
  text-align: center;

  @media (min-width: ${({ theme }) => theme.breakpoints.mobileLarge}) {
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
    margin: 0 0 ${({ theme }) => theme.spacing.md} 0;
    letter-spacing: 1px;
  }
`;

const DetailSubTitle = styled.h5`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  margin: ${({ theme }) => theme.spacing.md} 0 ${({ theme }) => theme.spacing.sm} 0;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  opacity: 0.9;
  line-height: 1.4;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};

  @media (min-width: ${({ theme }) => theme.breakpoints.mobileLarge}) {
    margin: ${({ theme }) => theme.spacing.lg} 0 ${({ theme }) => theme.spacing.sm} 0;
    letter-spacing: 0.8px;
  }

  svg {
    flex-shrink: 0;
  }
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  gap: ${({ theme }) => theme.spacing.sm};
  flex-wrap: wrap;
  line-height: 1.5;

  &:last-child {
    margin-bottom: 0;
  }
`;

const DetailRowCentered = styled(DetailRow)`
  margin-top: ${({ theme }) => theme.spacing.md};
  justify-content: center;
  text-align: center;
`;

const DetailRowDate = styled(DetailRow)`
  flex-direction: column;
  align-items: flex-start;
  
  @media (min-width: ${({ theme }) => theme.breakpoints.mobileLarge}) {
    flex-direction: row;
    align-items: center;
  }
`;

const DetailSection = styled.div`
  margin-top: ${({ theme }) => theme.spacing.md};
  padding-top: ${({ theme }) => theme.spacing.md};
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  
  &:first-of-type {
    margin-top: 0;
    padding-top: 0;
    border-top: none;
  }
`;

const DetailLabel = styled.span`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  line-height: 1.5;
  flex-shrink: 0;

  @media (min-width: ${({ theme }) => theme.breakpoints.mobileLarge}) {
    font-size: ${({ theme }) => theme.typography.fontSize.base};
  }
`;

const DetailValue = styled.span`
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  line-height: 1.5;
  text-align: right;
  word-break: break-word;

  @media (min-width: ${({ theme }) => theme.breakpoints.mobileLarge}) {
    font-size: ${({ theme }) => theme.typography.fontSize.base};
  }
`;

const DetailValueError = styled(DetailValue)`
  color: ${({ theme }) => theme.colors.text.error || '#EF4444'};
`;

const DetailValueWarning = styled(DetailValue)`
  color: #FF4444;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  width: 100%;
  text-align: center;

  @media (min-width: ${({ theme }) => theme.breakpoints.mobileLarge}) {
    font-size: ${({ theme }) => theme.typography.fontSize.base};
  }
`;

const DetailValueAccent = styled(DetailValue)`
  color: #60A5FA;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  min-width: 100px;
  text-align: right;
  font-variant-numeric: tabular-nums;

  @media (min-width: ${({ theme }) => theme.breakpoints.mobileLarge}) {
    min-width: 120px;
  }
`;

const DetailValueSuccess = styled(DetailValue)`
  color: #10b981;
`;

const DetailValueDateSuccess = styled(DetailValueSuccess)`
  text-align: left;
  width: 100%;
  
  @media (min-width: ${({ theme }) => theme.breakpoints.mobileLarge}) {
    text-align: left;
  }
`;

const DetailValueDateError = styled(DetailValueError)`
  text-align: left;
  width: 100%;
  font-weight: ${({ theme }) => theme.typography.fontWeight.normal};
  
  @media (min-width: ${({ theme }) => theme.breakpoints.mobileLarge}) {
    text-align: left;
  }
`;

const DetailValuePrimary = styled(DetailValue)`
  color: ${({ theme }) => theme.colors.primary};
  font-family: ${({ theme }) => theme.typography.fontFamily.mono};
`;

const LocationValueWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  vertical-align: middle;

  @media (min-width: ${({ theme }) => theme.breakpoints.mobileLarge}) {
    gap: ${({ theme }) => theme.spacing.sm};
  }
`;

const FlagIconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  margin-right: ${({ theme }) => theme.spacing.xs};
  vertical-align: middle;
  flex-shrink: 0;

  @media (min-width: ${({ theme }) => theme.breakpoints.mobileLarge}) {
    margin-right: ${({ theme }) => theme.spacing.sm};
  }

  svg, img {
    width: 16px;
    height: 12px;
    border-radius: 2px;
    display: inline-block;
    vertical-align: middle;

    @media (min-width: ${({ theme }) => theme.breakpoints.mobileLarge}) {
      width: 20px;
      height: 15px;
    }
  }
`;

const CountdownDisplay = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-top: ${({ theme }) => theme.spacing.md};
`;

const CountdownUnit = styled.div`
  text-align: center;
  flex: 1;
`;

const CountdownValue = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 1.2;

  @media (min-width: ${({ theme }) => theme.breakpoints.mobileLarge}) {
    font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: ${({ theme }) => theme.typography.fontSize['4xl']};
    line-height: 1;
  }
`;

const CountdownLabel = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-transform: lowercase;
  margin-top: ${({ theme }) => theme.spacing.xs};
  line-height: 1.4;

  @media (min-width: ${({ theme }) => theme.breakpoints.mobileLarge}) {
    margin-top: 4px;
  }
`;

const TimerContainerWrapper = styled.div`
  padding: ${({ theme }) => theme.spacing.md} 0;
  margin: ${({ theme }) => theme.spacing.md} 0;

  @media (min-width: ${({ theme }) => theme.breakpoints.mobileLarge}) {
    padding: ${({ theme }) => theme.spacing.lg} 0;
    margin: ${({ theme }) => theme.spacing.lg} 0;
  }
`;

const TimerWrapperStyled = styled(TimerWrapper)`
  gap: ${({ theme }) => theme.spacing.sm};

  @media (min-width: ${({ theme }) => theme.breakpoints.mobileLarge}) {
    gap: ${({ theme }) => theme.spacing.md};
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    gap: 12px;
  }
`;

const ActionButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-top: ${({ theme }) => theme.spacing.lg};
  width: 100%;

  @media (min-width: ${({ theme }) => theme.breakpoints.mobileLarge}) {
    gap: ${({ theme }) => theme.spacing.md};
    margin-top: ${({ theme }) => theme.spacing.xl};
  }
`;

const BaseButton = styled(motion.button)`
  width: 100%;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.xs};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.normal};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  min-height: ${({ theme }) => theme.mobile.touchTarget};
  line-height: 1.4;

  @media (min-width: ${({ theme }) => theme.breakpoints.mobileLarge}) {
    font-size: ${({ theme }) => theme.typography.fontSize.base};
    padding: ${({ theme }) => theme.spacing.md};
    gap: ${({ theme }) => theme.spacing.sm};
    min-height: ${({ theme }) => theme.mobile.touchTargetLarge};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const PrimaryButton = styled(BaseButton)`
  background: linear-gradient(135deg, #2563EB, #1E40AF);
  color: ${({ theme }) => theme.colors.text.primary};
  border: none;
  box-shadow: ${({ theme }) => theme.shadows.md};
  position: relative;
  overflow: hidden;

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #3B82F6, #2563EB);
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.lg};
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: ${({ theme }) => theme.shadows.sm};
  }

  &:disabled {
    box-shadow: ${({ theme }) => theme.shadows.sm};
  }
`;

const SecondaryButton = styled(BaseButton)`
  background: transparent;
  color: ${({ theme }) => theme.colors.primary};
  border: 1px solid ${({ theme }) => theme.colors.primary};

  &:hover:not(:disabled) {
    background: rgba(59, 130, 246, 0.1);
    border-color: ${({ theme }) => theme.colors.primary};
    transform: translateY(-2px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }
`;

const ResetButton = styled(BaseButton)`
  background: transparent;
  color: ${({ theme }) => theme.colors.text.error || '#EF4444'};
  border: 1px solid rgba(239, 68, 68, 0.4);

  &:hover:not(:disabled) {
    background: rgba(239, 68, 68, 0.1);
    border-color: rgba(239, 68, 68, 0.6);
    transform: translateY(-2px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }
`;

const ContactSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  text-align: center;

  @media (min-width: ${({ theme }) => theme.breakpoints.mobileLarge}) {
    gap: ${({ theme }) => theme.spacing.sm};
  }
`;

const FooterIconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.spacing.xs || '4px'};

  @media (min-width: ${({ theme }) => theme.breakpoints.mobileLarge}) {
    margin-bottom: ${({ theme }) => theme.spacing.xs || '8px'};
  }
`;

const ContactText = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  margin: 0;
  line-height: 1.5;

  @media (min-width: ${({ theme }) => theme.breakpoints.mobileLarge}) {
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
  }
`;

const WarningMessage = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.md};
  background: rgba(245, 158, 11, 0.1);
  border: 1px solid #F59E0B;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  margin-top: ${({ theme }) => theme.spacing.lg};
  color: #F59E0B;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;


export const VisaStatus = ({
  visaDuration,
  selectedDate,
  selectedCountry,
  status,
  expiryDate,
  remainingTime,
  localTime,
  localDate,
  localCity,
  localTimezone,
  onReset,
  timeAccuracy,
  location
}) => {
  const isExpired = status === 'Invalid';
  const [locationData, setLocationData] = useState(location || null);

  // Fetch location data if not provided
  useEffect(() => {
    if (!locationData) {
      const fetchLocation = async () => {
        try {
          const data = await locationService.getLocationData();
          console.log('Location data received in VisaStatus:', data);
          if (data) {
            const locationDataObj = {
              userCity: data.city,
              userCountryName: data.country,
              userCountryCode: data.countryCode ? data.countryCode.toUpperCase() : data.countryCode,
              userIP: data.ip,
              // Also include raw fields as fallback
              city: data.city,
              country: data.country,
              countryCode: data.countryCode ? data.countryCode.toUpperCase() : data.countryCode,
              ip: data.ip
            };
            setLocationData(locationDataObj);
            console.log('Set locationData in VisaStatus:', locationDataObj);
          } else {
            console.warn('No location data received in VisaStatus');
          }
        } catch (error) {
          console.error('Failed to fetch location data:', error);
        }
      };
      fetchLocation();
    }
  }, [locationData]);

  // Handle Add to Calendar functionality
  const handleAddToCalendar = () => {
    try {
      // Validate required data
      if (!expiryDate || !selectedDate || !selectedCountry) {
        console.error('Missing required data for calendar generation');
        // Could show user-friendly error message here
        alert('Unable to generate calendar event. Please ensure all visa information is available.');
        return;
      }

      // Prepare visa data for calendar generation
      const visaData = {
        expiryDate,
        selectedDate,
        selectedCountry,
        visaDuration,
        status: status || 'Valid'
      };

      // Generate and download calendar file
      const result = generateAndDownloadCalendar(visaData);

      if (result.success) {
        console.log('Calendar file generated successfully:', result.filename);
        // Optionally show success message to user
      } else {
        console.error('Failed to generate calendar file:', result.error);
        alert('Failed to generate calendar file. Please try again.');
      }
    } catch (error) {
      console.error('Error in handleAddToCalendar:', error);
      alert('An error occurred while generating the calendar event. Please try again.');
    }
  };

  // Helper function to format country name with line break after "OF"
  const formatCountryName = (countryName) => {
    if (!countryName) return 'Destination';
    // Check if name contains " of " or " Of " (case-insensitive with spaces)
    const lowerName = countryName.toLowerCase();
    if (lowerName.includes(' of ')) {
      // Find the position of " of " in the original case
      const ofIndex = lowerName.indexOf(' of ');
      const beforeOf = countryName.substring(0, ofIndex);
      const afterOf = countryName.substring(ofIndex + 4); // +4 to skip " of "
      // Find " of " in original case (preserve case)
      const originalOf = countryName.substring(ofIndex, ofIndex + 4);
      return (
        <>
          {beforeOf}{originalOf}<br />
          {afterOf}
        </>
      );
    }
    // If no "of" pattern, return as is (will wrap naturally)
    return countryName;
  };

  return (
    <StatusContainer>
      <PageContainer
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <MainCard
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, type: "spring", stiffness: 100 }}
        >
          {/* Top Navigation */}
          <BackButton onClick={onReset}>
            <FiChevronLeft size={24} />
            Go Back
          </BackButton>

          {/* Country Information Card */}
          {selectedCountry?.countryCode && (
            <CountryCard>
              <DetailTitle>Issuing Authority</DetailTitle>
              <CountryInfoWrapper>
                <StatusFlagWrapper>
                  <ReactCountryFlag
                    countryCode={selectedCountry.countryCode}
                    svg
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                </StatusFlagWrapper>
                <StatusCountryName>
                  {formatCountryName(selectedCountry?.fullName || selectedCountry?.name)}
                </StatusCountryName>
              </CountryInfoWrapper>
            </CountryCard>
          )}

          {/* Visa Status Card */}
          <StatusCard $isExpired={isExpired}>
            <DetailTitle>Visa Status</DetailTitle>
            <StatusHeader>
              <StatusIconWrapper $isExpired={isExpired}>
                {isExpired ? <FiX /> : <FiCheck />}
              </StatusIconWrapper>
              <StatusHeaderContent>
                <StatusText>
                  <StatusValue $isExpired={isExpired}>{status.toUpperCase()}</StatusValue>
                </StatusText>
                {visaDuration && (
                  <StatusVisaDuration>{visaDuration}-Days Visa</StatusVisaDuration>
                )}
              </StatusHeaderContent>
            </StatusHeader>
          </StatusCard>

          {/* Countdown Card */}
          {!isExpired && remainingTime && (
            <DetailCard>
              <DetailTitle>Countdown</DetailTitle>
              <TimerContainerWrapper>
                <TimerContainer>
                  <TimerWrapperStyled>
                    <ResponsiveTimer
                      time={remainingTime.days}
                      unit="days"
                      duration={Number(visaDuration) * 86400}
                      remainingTime={remainingTime.totalSeconds}
                      gradientId="activeGradient"
                    />
                    <ResponsiveTimer
                      time={remainingTime.hours}
                      unit="hours"
                      duration={86400}
                      remainingTime={remainingTime.totalSeconds % 86400}
                      gradientId="activeGradient"
                    />
                    <ResponsiveTimer
                      time={remainingTime.minutes}
                      unit="min"
                      duration={3600}
                      remainingTime={remainingTime.totalSeconds % 3600}
                      gradientId="activeGradient"
                    />
                    <ResponsiveTimer
                      time={remainingTime.seconds}
                      unit="sec"
                      duration={60}
                      remainingTime={remainingTime.totalSeconds % 60}
                      gradientId="activeGradient"
                    />
                  </TimerWrapperStyled>
                </TimerContainer>
              </TimerContainerWrapper>
            </DetailCard>
          )}

          {/* Issuing Information Card */}
          <DetailCard>
            <DetailTitle>Issuing Information</DetailTitle>
            
            {/* Date Information Section */}
            <DetailSection>
              <DetailRowDate>
                <DetailLabel>Issue Date:</DetailLabel>
                <DetailValueDateSuccess>{formatDate(selectedDate)}</DetailValueDateSuccess>
              </DetailRowDate>
              <DetailRowDate>
                <DetailLabel>Expiry Date:</DetailLabel>
                <DetailValueDateError>
                  {formatExpirationDate(expiryDate, selectedCountry)}
                </DetailValueDateError>
              </DetailRowDate>
              {isExpired && (
                <DetailRowCentered>
                  <DetailValueWarning>
                    VISA EXPIRED {Math.abs(getDaysUntilExpiry(expiryDate))} DAYS AGO
                  </DetailValueWarning>
                </DetailRowCentered>
              )}
            </DetailSection>

            {/* Local Time Information Section */}
            {!isExpired && (localDate || localTime || localTimezone) && (
              <DetailSection>
                {localDate && (
                  <DetailRow>
                    <DetailLabel>Local Date:</DetailLabel>
                    <DetailValueAccent>{localDate}</DetailValueAccent>
                  </DetailRow>
                )}
                {localTime && (
                  <DetailRow>
                    <DetailLabel>Local Time:</DetailLabel>
                    <DetailValueAccent>{localTime}</DetailValueAccent>
                  </DetailRow>
                )}
                {localTimezone && (
                  <DetailRow>
                    <DetailLabel>Local Time Zone:</DetailLabel>
                    <DetailValueAccent>{localTimezone}</DetailValueAccent>
                  </DetailRow>
                )}
              </DetailSection>
            )}

            {/* IP Based Location */}
            {locationData && (locationData.userCity || locationData.userCountryName || locationData.userCountryCode || locationData.city || locationData.country || locationData.countryCode || locationData.userIP || locationData.ip) && (
              <DetailSection>
                {(() => {
                  // Check both transformed field names and raw field names from service
                  const city = locationData.userCity || locationData.city;
                  const country = locationData.userCountryName || locationData.country;
                  const countryCode = locationData.userCountryCode || locationData.countryCode;
                  const ip = locationData.userIP || locationData.ip;
                  
                  let locationText = '';
                  // Prioritize showing countryCode when available, even without city
                  if (city && countryCode) {
                    locationText = `${city}, ${countryCode.toUpperCase()}`;
                  } else if (countryCode) {
                    // Show countryCode even if city is missing (e.g., Morocco "MA")
                    locationText = countryCode.toUpperCase();
                    // Add country name if available for better context
                    if (country && country !== countryCode) {
                      locationText = `${countryCode.toUpperCase()} (${country})`;
                    }
                  } else if (city && country) {
                    locationText = `${city}, ${country}`;
                  } else if (country) {
                    locationText = country;
                  } else if (city) {
                    locationText = city;
                  }
                  
                  // Always show IP if available, even if locationText is empty
                  if (!locationText && !ip) {
                    return null;
                  }
                  
                  return (
                    <>
                      {locationText && (
                        <DetailRow>
                          <DetailLabel>IP Based Location:</DetailLabel>
                          <DetailValueSuccess>
                            <LocationValueWrapper>
                              {countryCode && (
                                <FlagIconWrapper>
                                  <ReactCountryFlag
                                    countryCode={countryCode}
                                    svg
                                  />
                                </FlagIconWrapper>
                              )}
                              {locationText}
                            </LocationValueWrapper>
                          </DetailValueSuccess>
                        </DetailRow>
                      )}
                      {ip && (
                        <DetailRow>
                          <DetailLabel>Browser IP Address:</DetailLabel>
                          <DetailValuePrimary>{ip}</DetailValuePrimary>
                        </DetailRow>
                      )}
                    </>
                  );
                })()}
              </DetailSection>
            )}
          </DetailCard>

          {/* Action Buttons */}
          {!isExpired && (
            <ActionButtonGroup>
              <PrimaryButton
                onClick={handleAddToCalendar}
              >
                <FiCalendar /> Add to Calendar
              </PrimaryButton>
              <ResetButton
                onClick={onReset}
              >
                <FiRefreshCw /> Reset
              </ResetButton>
            </ActionButtonGroup>
          )}

          {/* Footer Branding */}
          <FooterSection>
            <ContactSection>
              <FooterIconContainer>
                <TimeAccuracyInfo timeAccuracy={timeAccuracy} />
              </FooterIconContainer>
              <ContactText>Powered by Visa Checker Pro v2.4</ContactText>
            </ContactSection>
          </FooterSection>
        </MainCard>

        {/* Global SVG Definitions for Timer Gradients */}
        <GradientDefs>
          <defs>
            <linearGradient id="activeGradient" x1="1" y1="0" x2="0" y2="0">
              <stop offset="0%" stopColor="#30cfd0" />
              <stop offset="50%" stopColor="#00FF85" />
              <stop offset="100%" stopColor="#0079FF" />
            </linearGradient>
            <linearGradient id="activeGradientInactive" x1="1" y1="0" x2="0" y2="0">
              <stop offset="0%" stopColor="#30cfd0" stopOpacity="0.5" />
              <stop offset="50%" stopColor="#00FF85" stopOpacity="0.5" />
              <stop offset="100%" stopColor="#0079FF" stopOpacity="0.5" />
            </linearGradient>
          </defs>
        </GradientDefs>
      </PageContainer>
    </StatusContainer>
  );
};
